import type { CSSVariablesResolver, MantineTheme } from '@mantine/core';
import { alpha, getPrimaryShade, isVirtualColor, v8CssVariablesResolver } from '@mantine/core';

/** Design system dark ramp: gray-0 stays the faintest tint and gray-9 the strongest text in both schemes. */
export const DARK_GRAY_RAMP = [
	'#2e2e2e',
	'#343434',
	'#3b3b3b',
	'#424242',
	'#545454',
	'#696969',
	'#909090',
	'#b0b0b0',
	'#bdbdbd',
	'#c9c9c9',
] as const;

// Mantine's dark gray variants point at the ramp through var(), so flipping the ramp would turn a
// `variant="light" color="gray"` label into #424242 on a dark surface. Restate them as literals.
function pinnedGrayVariants(theme: MantineTheme) {
	const gray = theme.colors.gray;
	const shade = getPrimaryShade(theme, 'dark');
	const lightShade = Math.max(0, shade - 2);
	const outlineShade = Math.max(0, shade - 4);

	return {
		'--mantine-color-gray-text': gray[4],
		'--mantine-color-gray-filled': gray[shade],
		'--mantine-color-gray-filled-hover': gray[shade === 9 ? 8 : shade + 1],
		'--mantine-color-gray-light': alpha(gray[lightShade], 0.15),
		'--mantine-color-gray-light-hover': alpha(gray[lightShade], 0.2),
		'--mantine-color-gray-light-color': gray[Math.max(0, shade - 5)],
		'--mantine-color-gray-outline': gray[outlineShade],
		'--mantine-color-gray-outline-hover': alpha(gray[outlineShade], 0.05),
	};
}

const UNWASHED_COLORS = new Set(['gray', 'dark']);

// The app paints shades 0 and 1 only as panel and selection backgrounds, which stock dark leaves pastel.
// Virtual colours are skipped: their tuples are var() references, not a ramp.
function tintWashes(theme: MantineTheme) {
	return Object.fromEntries(
		Object.entries(theme.colors)
			.filter(([name, tuple]) => !UNWASHED_COLORS.has(name) && !isVirtualColor(tuple))
			.flatMap(([name, tuple]) => [
				[`--mantine-color-${name}-0`, alpha(tuple[6], 0.15)],
				[`--mantine-color-${name}-1`, alpha(tuple[6], 0.22)],
			]),
	);
}

export const appCssVariablesResolver: CSSVariablesResolver = theme => {
	const base = v8CssVariablesResolver(theme);
	const flippedRamp = Object.fromEntries(DARK_GRAY_RAMP.map((hex, i) => [`--mantine-color-gray-${i}`, hex]));

	return {
		...base,
		dark: {
			...base.dark,
			...flippedRamp,
			...pinnedGrayVariants(theme),
			...tintWashes(theme),
			// Mantine's dark tooltip falls back to gray-2 behind black text, so the flipped ramp would make it unreadable.
			'--tooltip-bg': theme.colors.gray[2],
			// v8 draws c="dark" with dark-4 and dark subtle variants with dark-3, both close to the dark surface.
			'--mantine-color-dark-text': 'var(--mantine-color-text)',
			'--mantine-color-dark-light-color': 'var(--mantine-color-text)',
		},
	};
};
